
import { gql } from '@apollo/client';
import type {RelayConnection} from 'common';
import {useLazyQuery$} from 'graphqlsupport';
import type {Offer} from 'offer/domain/types';
import type {Direction, Sort} from 'pagination';
import {useEffect} from 'react';
import type {Referral} from 'referral/domain/types';
import {combineLatest} from 'rxjs';
import type {BehaviorSubject} from 'rxjs';
import type {RelayPaginationParams} from '../pagination';
import type {ReferralFilterParams} from '../filter';

export const REFERRAL_DETAIL_FOR_PROVIDER_FRAGMENT = {
    name: 'referralDetailForProvider',
    value: `
fragment referralDetailForProvider on Referral {
    id
    guid
    caseNumber
    clientName
    serviceTypes
    referralConsiderations
    status
    placementType
    location
    clientAge
    serviceLocations
    serviceLocationsParaprofessional
    serviceLocationsMidLevel
    serviceLocationsHighLevel
    serviceLocationsGroupCare
    requiredLanguage
    directCareHours
    midLevelHours
    highLevelHours
    groupCareHours
    authDirectCareHours
    authMidLevelHours
    authHighLevelHours
    authGroupCareHours
    authDirectCareBeneficialHours
    authMidLevelBeneficialHours
    authHighLevelBeneficialHours
    authGroupCareBeneficialHours
    careMediatedMidHours
    careMediatedMidMinHours
    careMediatedHighHours
    careMediatedHighMinHours
    practDirectBeneficialHours
    practMidBeneficialHours
    practHighBeneficialHours
    practGroupBeneficialHours
    practDirectOptimalHours
    practMidOptimalHours
    practHighOptimalHours
    practGroupOptimalHours
    authTherapyTreatmentUnitsPer
    authTherapyTreatmentUnitType
    authTherapyTreatmentUnit
    scheduleNotes
    county
    clientSiblingReferrals {
        guid
        siblingCaseNumber
        siblingReferralGuid
        clientReferralGuid
        hasOffers
    }
    myProviderPreferences {
        guid
        preferenceReason
    }
}
`
}

export type ReferralInquiryRequestForProvider = {
    guid: string;
    caseNumber: string;
    status: string;
    submittedDate: string;
};
export type ReferralWithMyOrgOffers = Referral & {
    myOrgSubmittedOffers?: Array<Pick<Offer, 'id' | 'guid' | 'conversationSummary'>> | null;
    myOrgAcceptedOffers?: Array<Pick<Offer, 'id' | 'guid' | 'submittedAt' | 'conversationSummary'>> | null;
    incompleteReferralInquiryRequests?: ReferralInquiryRequestForProvider[] | null;
};
export type QueryableReferralStatus = 'AVAILABLE' | 'ACCEPTED';

const VALID_SORT_COLUMNS: Record<QueryableReferralStatus, ReadonlyArray<keyof Referral>> = {
    AVAILABLE: ['caseNumber', 'clientName', 'serviceTypes', 'placementType', 'county', 'location', 'clientAge', 'requiredLanguage'],
    ACCEPTED: ['caseNumber', 'clientName', 'serviceTypes', 'placementType', 'requestDate', 'matchedDate', 'location'],
};

export const isValidCombination = (sort: Sort<Referral>, status: QueryableReferralStatus): boolean =>
    VALID_SORT_COLUMNS[status].includes(sort.by);

export type ReferralForProviderInput = {
    status: QueryableReferralStatus;
    withSubmittedOffers: boolean,
    withAcceptedOffers: boolean,
    by: keyof Referral;
    direction: Direction;
    selectedReferralUuid: string | null;
    offerConversationOnly: boolean;
} & RelayPaginationParams & ReferralFilterParams;
export type ReferralForProviderResult = {
    providerReferralQuery: RelayConnection<ReferralWithMyOrgOffers> & { totalElements: number };
};
const REFERRAL_FOR_PROVIDER_QUERY = gql`
query referralByStatusAndMyOrgOffers(
    $status: ProviderReferralQueryableStatus = AVAILABLE,
    $withSubmittedOffers: Boolean = false,
    $withAcceptedOffers: Boolean = false,
    $by: String = "caseNumber",
    $direction: String = "DESC",
    $first: Int = 20,
    $after: String,
    $placementType: [String],
    $serviceType: [String],
    $counties: [String],
    $serviceLocation: [String],
    $selectedReferralUuid: String,
    $serviceLocationType: [String],
    $offerConversationOnly: Boolean
) {
  providerReferralQuery(
    params: {
      referralStatus: $status
      sortDir: $direction
      sortBy: $by
      counties: $counties,
      placementType: $placementType,
      serviceType: $serviceType,
      serviceLocation: $serviceLocation,
      selectedReferralUuid: $selectedReferralUuid,
      serviceLocationType: $serviceLocationType,
      offerConversationOnly: $offerConversationOnly
    }
    first: $first
    after: $after
  ) {
    edges {
      cursor
      node {
        ...${REFERRAL_DETAIL_FOR_PROVIDER_FRAGMENT.name}
        myOrgSubmittedOffers: myOrgOffers(offerStatus: SUBMITTED) @include(if: $withSubmittedOffers) {
          id
          guid
          conversationSummary {
            urgent
            hasConversation
            providerUnreadCount
            clientPlacementTeamUnreadCount
          }
        }
        myOrgAcceptedOffers: myOrgOffers(offerStatus: ACCEPTED) @include(if: $withAcceptedOffers) {
          id
          guid
          submittedAt
          conversationSummary {
            urgent
            hasConversation
            providerUnreadCount
            clientPlacementTeamUnreadCount
          }
        }
        requestDate 
        matchedDate @include(if: $withAcceptedOffers)
        incompleteReferralInquiryRequests {
            guid
            caseNumber
            status
            submittedDate
        }
      }
    }
    totalElements
    pageInfo {
      hasPreviousPage
      endCursor
      hasNextPage
      startCursor
    }
  }
}
${REFERRAL_DETAIL_FOR_PROVIDER_FRAGMENT.value}
`;
const queryParams = (
    currentStatus: QueryableReferralStatus,
    sort: Sort<Referral>,
    pagination: RelayPaginationParams,
    filter: ReferralFilterParams,
    selectedReferralUuid: string | null,
    offerConversationOnly: boolean
): ReferralForProviderInput => ({
    status: currentStatus,
    withAcceptedOffers: currentStatus === 'ACCEPTED',
    withSubmittedOffers: currentStatus === 'AVAILABLE',
    selectedReferralUuid,
    offerConversationOnly,
    ...sort,
    ...pagination,
    ...filter,
});

export const useReferralQueryForProvider$ = (
    currentStatus$: BehaviorSubject<QueryableReferralStatus>,
    currentSort$: BehaviorSubject<Sort<Referral>>,
    pagination: RelayPaginationParams,
    referralFilterParams: ReferralFilterParams,
    selectedReferralUuid: string | null,
    offerConversationOnly: boolean
) => {
    const {
        isRunning$,
        execute,
        data,
        error,
    } = useLazyQuery$<ReferralForProviderInput, ReferralForProviderResult>(
        REFERRAL_FOR_PROVIDER_QUERY,
        queryParams(currentStatus$.getValue(), currentSort$.getValue(), pagination, referralFilterParams, selectedReferralUuid, offerConversationOnly)
    );

    useEffect(() => {
        const autoFetchUponSortChange = combineLatest([currentSort$, currentStatus$])
            .subscribe(([sort, status]) => {
                if (isValidCombination(sort, status)) {
                    execute({
                        status,
                        ...sort,
                    });
                }
            });
        return () => autoFetchUponSortChange.unsubscribe();
    }, [execute, currentSort$, currentStatus$]);

    return {
        isRunning$,
        execute,
        data,
        error,
    };
}
